import { useState } from 'react';
import api from '../lib/api';
import { Button } from './ui/button';
import { Download, FileSpreadsheet, FileText, FileType, ChevronDown } from 'lucide-react';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from './ui/dropdown-menu';
import { toast } from 'sonner';

/**
 * Reusable export dropdown - downloads CSV / XLSX / PDF.
 *
 * Props:
 *   endpoint: e.g. "/exports/clients" (no /api prefix; api lib adds it)
 *   params:   plain object of query params (excluding format)
 *   filename: filename base (no extension)
 *   testIdPrefix: prefix for data-testid
 */
export default function ExportMenu({ endpoint, params = {}, filename = 'export', testIdPrefix = 'export' }) {
  const [busy, setBusy] = useState(false);

  const download = async (format) => {
    setBusy(true);
    try {
      const query = new URLSearchParams({ ...params, format }).toString();
      const res = await api.get(`${endpoint}?${query}`, { responseType: 'blob' });
      const blob = new Blob([res.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${filename}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (e) {
      toast.error('Export failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="gap-2 border-[#1B2A1B] text-[#1B2A1B] hover:bg-[#1B2A1B] hover:text-white"
          disabled={busy}
          data-testid={`${testIdPrefix}-export-btn`}
        >
          <Download size={16} />
          {busy ? 'Exporting...' : 'Export'}
          <ChevronDown size={14} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        <DropdownMenuItem
          onClick={() => download('xlsx')}
          className="gap-2 cursor-pointer"
          data-testid={`${testIdPrefix}-export-xlsx`}
        >
          <FileSpreadsheet size={15} className="text-green-700" /> Excel (.xlsx)
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => download('csv')}
          className="gap-2 cursor-pointer"
          data-testid={`${testIdPrefix}-export-csv`}
        >
          <FileType size={15} className="text-blue-700" /> CSV (.csv)
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => download('pdf')}
          className="gap-2 cursor-pointer"
          data-testid={`${testIdPrefix}-export-pdf`}
        >
          <FileText size={15} className="text-[#D4AF37]" /> PDF (.pdf)
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
